import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AppTopBar } from 'components/AppTopBar';
import { Text } from 'components/Text';
import { TrackingEvents, useTrackingDispatch } from 'modules/analytics';
import { BrazeContentFeed } from 'modules/content-cards-feed';
import { getTrans } from 'modules/i18n';
import { Layout } from 'modules/minimal-ui-theme';
import {
  defaultPreferencesDisabled,
  defaultPreferencesEnabled,
  isDismissed,
  NotificationPreferencesResponse,
  NotificationPrompt,
  useBraze,
  useNotificationPrompt,
  usePreferencesMutation,
  usePreferencesQuery
} from 'modules/push-notification';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { WorkWeeks } from 'modules/work-weeks';

export const WeeksPage = () => {
  const dispatch = useTrackingDispatch();
  const { user } = useAuth0();
  const firstName = user?.given_name ?? user?.name?.split(' ')[0];
  const { isPromptVisible, closePrompt } = useNotificationPrompt();
  const braze = useBraze();
  const preferencesMutation = usePreferencesMutation();
  const { data, isLoading: isPreferencesLoading } = usePreferencesQuery();

  const isSupported = braze?.isPushSupported() ?? false;
  const isBrowserSupported = 'Notification' in window;
  const hasPermission = braze?.isPushPermissionGranted() ?? false;

  const { isEnabled: isPushEnabled, isLoading } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_PUSH_ENABLED
  );

  const isPromptDismissed = isDismissed();

  const isNotificationPromptVisible = calculateNotificationPromptVisibility(
    isPushEnabled,
    isSupported && isBrowserSupported,
    isPromptVisible,
    hasPermission,
    data
  );

  useEffect(() => {
    dispatch({
      type: TrackingEvents.WEEKS_OVERVIEW_PAGE_VIEWED
    });
  }, [dispatch]);

  useEffect(() => {
    if (isPushEnabled && !isPreferencesLoading) {
      const permission =
        'Notification' in window && Notification.permission
          ? Notification.permission
          : null;

      if (permission === 'granted') {
        preferencesMutation.mutate({
          ...defaultPreferencesEnabled,
          pushPermission: true,
          refId: data?.data?.notifications?.refId ?? ''
        });
      } else if (permission === 'denied' || isPromptDismissed) {
        preferencesMutation.mutate({
          ...defaultPreferencesDisabled,
          pushPermission: false,
          refId: data?.data?.notifications?.refId ?? ''
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isPreferencesLoading]);

  if (isNotificationPromptVisible) {
    return <NotificationPrompt close={closePrompt} />;
  }

  return (
    <Layout header={<AppTopBar variant="brand" />}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: 'text.primary' }}
        >
          {getTrans('weeksOverview.title')}
        </Typography>
        <Text variant="body2" color="text.secondary" data-dd-privacy="mask">
          {firstName
            ? `Hi ${firstName} – ${getTrans('weeksOverview.greeting')}`
            : getTrans('weeksOverview.greeting')}
        </Text>
      </Box>
      <BrazeContentFeed />
      <WorkWeeks />
    </Layout>
  );
};

const calculateNotificationPromptVisibility = (
  isPushEnabled: boolean,
  isSupported: boolean,
  isPromptVisible: boolean,
  hasPermission: boolean,
  data: NotificationPreferencesResponse | undefined
): boolean => {
  return (
    isPushEnabled && isSupported && isPromptVisible && !hasPermission && !!data
  );
};
